import type { SlashCommand } from '../../../../../api/agents-client/agent-client.type';
import { CommandScope, type GroupedCommands } from './agent-commands.type';

/** Color for scope badge text */
const SCOPE_BADGE_COLORS: Record<CommandScope, string> = {
  [CommandScope.Global]: 'var(--ink-lighter)',
  [CommandScope.Organization]: 'var(--secondary-base)',
  [CommandScope.User]: 'var(--primary-base)',
};

/** Background for scope badges */
const SCOPE_BADGE_BG: Record<CommandScope, string> = {
  [CommandScope.Global]: 'var(--sky-lighter)',
  [CommandScope.Organization]: 'var(--secondary-lightest)',
  [CommandScope.User]: 'var(--primary-lightest)',
};

/** Short label for scope badges */
const SCOPE_BADGE_LABEL: Record<CommandScope, string> = {
  [CommandScope.Global]: 'Global',
  [CommandScope.Organization]: 'Organization',
  [CommandScope.User]: 'Personal',
};

/**
 * Determines the scope of a slash command based on its organization and user fields
 * @param {SlashCommand} command - The command to determine the scope of
 * @returns {CommandScope} The scope of the command
 */
function getCommandScope(command: SlashCommand): CommandScope {
  if (command.organization === null && command.user === null) {
    return CommandScope.Global;
  }
  if (command.user === null) {
    return CommandScope.Organization;
  }
  return CommandScope.User;
}

/**
 * Groups commands by scope in display order: Global, Organization, My Commands
 * @param {SlashCommand[]} commands - The commands to group
 * @returns {GroupedCommands[]} The grouped commands
 */
function groupCommandsByScope(commands: SlashCommand[]): GroupedCommands[] {
  const groups: GroupedCommands[] = [];
  const scopeOrder = [CommandScope.Global, CommandScope.Organization, CommandScope.User];

  for (const scope of scopeOrder) {
    const scopeCommands = commands.filter((cmd) => getCommandScope(cmd) === scope);
    if (scopeCommands.length > 0) {
      groups.push({ scope, label: SCOPE_BADGE_LABEL[scope], commands: scopeCommands });
    }
  }

  return groups;
}

/**
 * Checks if a command can be edited by the current user
 * @param {SlashCommand} command - The command to check
 * @param {boolean} isAdmin - Whether the current user is an admin
 * @param {string | undefined} userId - The ID of the current user
 * @returns {boolean} Whether the command can be edited by the current user
 */
function canEditCommand(command: SlashCommand, isAdmin: boolean, userId: string | undefined): boolean {
  const scope = getCommandScope(command);
  if (scope === CommandScope.Global) return false;
  if (scope === CommandScope.Organization) return isAdmin;
  return command.user === userId;
}

const COMMAND_TOKEN_BOUNDARY = '(^|\\s)(/COMMAND_NAME)(?=$|\\s)';

/**
 * Escapes regex special characters in a string.
 * @param {string} value - Raw text to escape for regex usage.
 * @returns {string} Escaped text safe to interpolate into RegExp patterns.
 */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Builds a regex that matches a slash command as a standalone token.
 * Token boundaries are start-of-string/whitespace before and end-of-string/whitespace after.
 * @param {string} commandName - Slash command name without leading `/`.
 * @returns {RegExp} Boundary-aware regex for the slash command token.
 */
function getCommandTokenRegex(commandName: string): RegExp {
  const escapedCommandName = escapeRegExp(commandName);
  return new RegExp(COMMAND_TOKEN_BOUNDARY.replace('COMMAND_NAME', escapedCommandName));
}

/**
 * Finds the index where a standalone slash command token starts.
 * @param {string} text - Full input text to scan.
 * @param {string} commandName - Slash command name without leading `/`.
 * @returns {number} Start index of the token (the `/` position), or -1 when not found.
 */
function findCommandTokenStartIndex(text: string, commandName: string): number {
  const match = getCommandTokenRegex(commandName).exec(text);
  if (!match || match.index === undefined) {
    return -1;
  }

  // Skip the leading boundary capture (start-of-string or whitespace).
  return match.index + match[1].length;
}

/**
 * Checks if input text contains a standalone slash command token.
 * @param {string} text - Full input text to scan.
 * @param {string} commandName - Slash command name without leading `/`.
 * @returns {boolean} True when the command exists as a bounded token.
 */
function hasCommandToken(text: string, commandName: string): boolean {
  return findCommandTokenStartIndex(text, commandName) !== -1;
}

export {
  canEditCommand,
  findCommandTokenStartIndex,
  getCommandScope,
  groupCommandsByScope,
  hasCommandToken,
  SCOPE_BADGE_BG,
  SCOPE_BADGE_COLORS,
  SCOPE_BADGE_LABEL,
};
